import { useEffect, useState } from "react";

export const DEV_DATE_OVERRIDE_KEY = "ff.dev.asOfDate";
const LEGACY_OVERVIEW_DATE_OVERRIDE_KEY = "ff.overview.asOfDate";
const DEV_DATE_OVERRIDE_EVENT = "finance:dev-date-change";

type DevDateConsoleApi = {
  setDate: (date: string) => void;
  clearDate: () => void;
  getDate: () => string | null;
};

declare global {
  interface Window {
    financeDate?: DevDateConsoleApi;
    financeOverview?: DevDateConsoleApi;
  }
}

function assertDateString(date: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error("Use YYYY-MM-DD");
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) throw new Error("Use a valid YYYY-MM-DD date");
}

export function readDevDateOverride() {
  if (!import.meta.env.DEV) return null;
  const value = localStorage.getItem(DEV_DATE_OVERRIDE_KEY) || localStorage.getItem(LEGACY_OVERVIEW_DATE_OVERRIDE_KEY);
  if (value && localStorage.getItem(LEGACY_OVERVIEW_DATE_OVERRIDE_KEY)) {
    localStorage.setItem(DEV_DATE_OVERRIDE_KEY, value);
    localStorage.removeItem(LEGACY_OVERVIEW_DATE_OVERRIDE_KEY);
  }
  return value;
}

export function setDevDateOverride(date: string) {
  if (!import.meta.env.DEV) return;
  assertDateString(date);
  localStorage.setItem(DEV_DATE_OVERRIDE_KEY, date);
  window.dispatchEvent(new Event(DEV_DATE_OVERRIDE_EVENT));
}

export function clearDevDateOverride() {
  if (!import.meta.env.DEV) return;
  localStorage.removeItem(DEV_DATE_OVERRIDE_KEY);
  localStorage.removeItem(LEGACY_OVERVIEW_DATE_OVERRIDE_KEY);
  window.dispatchEvent(new Event(DEV_DATE_OVERRIDE_EVENT));
}

export function appToday() {
  const override = readDevDateOverride();
  return override ? new Date(`${override}T00:00:00`) : new Date();
}

export function appDateInputValue() {
  const date = appToday();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function installDevDateConsoleApi() {
  if (!import.meta.env.DEV) return;
  const api = {
    setDate: setDevDateOverride,
    clearDate: clearDevDateOverride,
    getDate: readDevDateOverride,
  };
  window.financeDate = api;
  window.financeOverview = api;
}

export function useDevDateOverride() {
  const [override, setOverride] = useState(readDevDateOverride);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    installDevDateConsoleApi();
    const sync = () => setOverride(readDevDateOverride());
    window.addEventListener(DEV_DATE_OVERRIDE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(DEV_DATE_OVERRIDE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return override;
}
